import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import { styles } from './PickerSmartphone.style';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_NAMES, ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import Spinner from '@photosi/rn-components/spinner/Spinner';
import QRCode from 'react-native-qrcode-svg';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { text } from 'src/theme/colors';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { useNavigation } from '@react-navigation/native';
import { navigationResetTo } from 'src/editor-v3/helpers/navigation.helper';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import { PickerProps } from '../Picker.types';

export const PickerSmartphone = observer(
  ({ onConfirmStep, isOnline, focusGroup, focusId, focusOrder }: PickerProps) => {
    const {
      i18n,
      project: {
        createShareTokenMobile,
        shareTokenMobile,
        setFromSmartphoneOrFamilyAndFriends,
        addProductsV2,
        activeProject,
        activeGroup,
        productConfigurationId,
        groupCode,
      },
      data: { percentage },
      drawer,
    } = useStore();

    const [url, setUrl] = useState<string>('');
    useEffect(() => {
      setFromSmartphoneOrFamilyAndFriends(true);
      if (isOnline) {
        if (!shareTokenMobile) {
          createShareTokenMobile().then(res => {
            generateUrl(res);
          });
        } else {
          generateUrl(shareTokenMobile);
        }
      }
    }, []);

    useEffect(() => {
      if (percentage > 0) onConfirmStep();
    }, [percentage]);

    const generateUrl = (token: string) => {
      if (!url.length) {
        const urlUploadMobile = `${EDITOR_BASE_URL}/${ROUTE_PATHS.UPLOAD_SMARTPHONE}?shareToken=${token}`;
        setUrl(urlUploadMobile);
      }
    };
    const navigation = useNavigation();
    const route = useTypedRoute<ROUTE_NAMES.Upload>();
    const params = route.params ?? {};
    const { groupCodeExternal, skipConfigurationStep, skipStepValue, ...filteredParams } = params;
    const navigateToGrid = async () => {
      activeProject?.deselectAllThumbnailImages();
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
        n_photo: 0,
        n_photos_facebook: 0,
        n_photos_gphoto: 0,
        n_photos_local: 0,
        n_photos_smartphone: 0,
        n_photos_family_friends: 0,
      });
      drawer.openDrawer({
        drawerPosition: DRAWER_POSITIONS.Left,
        drawerView: DRAWER_VIEWS.PhotoDrawer,
        withBackdrop: false,
        openPoint: 'button',
        uploadButtonCallback: async () => {
          activeGroup?.checkMinMaxProductsAndNotify();
          addProductsV2({
            ids: activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
            productConfigurationId: activeGroup ? activeGroup.currectProductConfigurationId : productConfigurationId,
            groupCode: activeGroup?.code || groupCode,
            groupId: activeGroup?.groupIdSDK,
          });
          drawer.closeDrawer();
        },
      });
      navigationResetTo(navigation, ROUTE_NAMES.EditorNew, { ...filteredParams });
    };
    return (
      <View style={styles.details}>
        <View style={styles.titleWrapper}>
          {url.length ? (
            <>
              <TextLato style={styles.mainTitle}>{i18n.t('upload:upload-qr-title')}</TextLato>
              <TextLato style={styles.description}>{i18n.t('upload:upload-qr-description')}</TextLato>
            </>
          ) : (
            <>
              <TextLato style={styles.mainTitle}>{i18n.t('upload:upload-qr-loading-title')}</TextLato>
              <TextLato style={styles.description}>{i18n.t('upload:upload-qr-loading-description')}</TextLato>
            </>
          )}
        </View>
        {url.length ? (
          <View style={styles.wrapper}>
            <View style={styles.qrCodeWrapper}>
              <QRCode size={168} value={url} />
            </View>
          </View>
        ) : (
          <View style={styles.wrapperLoader}>
            <View style={styles.qrCodeWrapper}>
              <Spinner />
            </View>
          </View>
        )}
        <View>
          <PrimaryButton
            onPress={navigateToGrid}
            text={i18n.t('upload:view-uploaded-photo')}
            focusId={focusId}
            focusGroup={focusGroup}
            focusOrder={focusOrder}
            style={{ width: 280 }}
            layout={BUTTON_LAYOUTS.CENTER}
            size={BUTTON_SIZES.REGULAR}
          />
        </View>
      </View>
    );
  },
);
